import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import profile from "../data/profile";
import recipes from "../data/recipes";
import devPosts from "../data/devPosts";
import devHacks from "../data/devHacks";
import RecipeCard from "../components/RecipeCard";
import DevCard from "../components/DevCard";
import HackCard from "../components/HackCard";
import SectionHead from "../components/SectionHead";
import NewsletterSection from "../components/NewsletterSection";
import styles from "./HomePage.module.css";

/* ── Typing hook ─────────────────────────────── */
function useTypingEffect(words, speed = 90, pause = 1800) {
  const [display, setDisplay] = useState("");
  const [wordIdx, setWordIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIdx];
    let t;
    if (!deleting && charIdx <= current.length) {
      t = setTimeout(() => {
        setDisplay(current.slice(0, charIdx));
        setCharIdx((c) => c + 1);
      }, speed);
    } else if (!deleting && charIdx > current.length) {
      t = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && charIdx >= 0) {
      t = setTimeout(() => {
        setDisplay(current.slice(0, charIdx));
        setCharIdx((c) => c - 1);
      }, speed / 2);
    } else {
      setDeleting(false);
      setWordIdx((i) => (i + 1) % words.length);
    }
    return () => clearTimeout(t);
  }, [charIdx, deleting, wordIdx, words, speed, pause]);

  return display;
}

/* ── Floating code snippets ─────────────────── */
const SNIPPETS = [
  "const bake = () => 🧁",
  'git commit -m "yum"',
  "npm run delicious",
  "<Recipe yield={12} />",
  "useState(🍪)",
  "async function proof()",
  "// TODO: eat this",
  "return <Croissant />",
  "import { butter }",
  "console.log(🍞)",
];

const HOBBIES = [
  {
    emoji: "🧁",
    label: "Baking",
    desc: "Recipes from scratch - no shortcuts, no backstories.",
    to: "/baking",
    bg: "linear-gradient(135deg,#fff0f8,#fce8f4)",
  },
  {
    emoji: "💻",
    label: "Dev Blog",
    desc: "Real talk about working in tech.",
    to: "/dev",
    bg: "linear-gradient(135deg,#f0f4ff,#e8eeff)",
  },
  {
    emoji: "⚡",
    label: "Dev Hacks",
    desc: "Code tips that change how you think.",
    to: "/hacks",
    bg: "linear-gradient(135deg,#f0fff8,#e0f8ec)",
  },
  {
    emoji: "🌿",
    label: "Slow Living",
    desc: "Books, travel, life beyond the screen.",
    to: "/about",
    bg: "linear-gradient(135deg,#fffbf0,#fff3e0)",
  },
];

export default function HomePage() {
  const typed = useTypingEffect(
    ["Baker.", "Developer.", "Creator.", "Human."],
    85,
    1600,
  );
  const featuredR = recipes.filter((r) => r.featured).slice(0, 3);
  const featuredP = devPosts.filter((p) => p.featured).slice(0, 3);
  const previewH = devHacks[0];
  const [tab, setTab] = useState("recipes");

  return (
    <div className={styles.root}>
      {/* ══════════════════════════════
          HERO
      ══════════════════════════════ */}
      <section className={styles.hero}>
        <div className={styles.meshBg} />
        <div className={styles.gridLines} />

        {/* floating code particles */}
        <div className={styles.particles} aria-hidden="true">
          {SNIPPETS.map((s, i) => (
            <span
              key={i}
              className={styles.particle}
              style={{
                left: `${5 + ((i * 9.3) % 88)}%`,
                top: `${8 + ((i * 14.1) % 78)}%`,
                animationDelay: `${i * 0.6}s`,
                animationDuration: `${7 + (i % 4)}s`,
              }}
            >
              {s}
            </span>
          ))}
        </div>

        <div className={`container ${styles.heroInner}`}>
          {/* LEFT */}
          <div className={styles.heroLeft}>
            {profile.available && (
              <div className={styles.availPill}>
                <span className={styles.availDot} /> {profile.availableText}
              </div>
            )}

            <h1 className={styles.h1}>
              <span className={styles.h1Line1}>I write code.</span>
              <span className={styles.h1Line2}>I bake bread.</span>
              <span className={styles.h1Line3}>
                I am a <span className={styles.typedWord}>{typed}</span>
                <span className={styles.cursor}>|</span>
              </span>
            </h1>

            <p className={styles.sub}>{profile.tagline}</p>

            <div className={styles.ctaRow}>
              <Link to="/baking" className={styles.btnPrimary}>
                🧁 Recipes
              </Link>
              <Link to="/dev" className={styles.btnSecondary}>
                💻 Dev Blog
              </Link>
              <Link to="/hacks" className={styles.btnGhost}>
                ⚡ Hacks
              </Link>
            </div>

            <div className={styles.counterRow}>
              {[
                { n: recipes.length, l: "Recipes", e: "🧁" },
                { n: devHacks.length, l: "Dev Hacks", e: "⚡" },
                { n: devPosts.length, l: "Posts", e: "💻" },
                { n: "6+", l: "Yrs Dev", e: "🚀" },
              ].map(({ n, l, e }) => (
                <div key={l} className={styles.counter}>
                  <span className={styles.ctrE}>{e}</span>
                  <span className={styles.ctrN}>{n}</span>
                  <span className={styles.ctrL}>{l}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT - profile card */}
          <div className={styles.heroRight}>
            <div className={styles.glowRing} />

            <div className={styles.profileCard}>
              {/* fake terminal bar */}
              <div className={styles.termBar}>
                <span className={styles.td} style={{ background: "#ff5f57" }} />
                <span className={styles.td} style={{ background: "#ffbd2e" }} />
                <span className={styles.td} style={{ background: "#28c840" }} />
                <span className={styles.termTitle}>profile.jsx</span>
              </div>

              <div className={styles.avatarWrap}>
                <div className={styles.avatarRing}>
                  <div className={styles.avatar}>
                    <img
                      src={profile.avatar}
                      alt="profile"
                      className={styles.avatarImg}
                    />
                  </div>
                </div>
                <span className={styles.onlineBadge}>online</span>
              </div>

              <h2 className={styles.cardName}>{profile.name}</h2>
              <p className={styles.cardRole}>{profile.title}</p>
              <p className={styles.cardLoc}>📍 {profile.location}</p>

              <div className={styles.cardDivider} />

              {/* Tech tags */}
              <p className={styles.tagHeading}>{"// tech stack"}</p>
              <div className={styles.tagRow}>
                {profile.techStack.map((t) => (
                  <span key={t} className={`${styles.tag} ${styles.tagTech}`}>
                    {t}
                  </span>
                ))}
              </div>

              {/* Baking tags */}
              <p className={styles.tagHeading} style={{ marginTop: 12 }}>
                {"// bakes"}
              </p>
              <div className={styles.tagRow}>
                {profile.bakingSpecialties.map((t) => (
                  <span key={t} className={`${styles.tag} ${styles.tagBake}`}>
                    {t}
                  </span>
                ))}
              </div>

              <Link to="/resume" className={styles.resumeBtn}>
                View Resume →
              </Link>
            </div>

            {/* status badges - sit BELOW card, never overlap */}
            <div className={styles.statusRow}>
              <div className={styles.statusBadge}>
                <span>🍰</span>
                <span>Latest recipe live ↗</span>
              </div>
              <div
                className={`${styles.statusBadge} ${styles.statusBadgePink}`}
              >
                <span>⚡</span>
                <span>New hack weekly</span>
              </div>
            </div>
          </div>
        </div>

        {/* curved wave */}
        <div className={styles.wave} aria-hidden="true">
          <svg
            viewBox="0 0 1440 110"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,40 C180,100 360,0 540,55 C720,110 900,10 1080,60 C1260,110 1350,30 1440,55 L1440,110 L0,110 Z"
              fill="#fff"
            />
          </svg>
        </div>
      </section>

      {/* ══════════════════════════════
          MARQUEE STRIP
      ══════════════════════════════ */}
      <div className={styles.marqueeWrap} aria-hidden="true">
        <div className={styles.marquee}>
          {[
            ...profile.techStack,
            ...profile.bakingSpecialties,
            ...profile.techStack,
            ...profile.bakingSpecialties,
          ].map((t, i) => (
            <span key={i} className={styles.mItem}>
              {i % 2 === 0 ? "💻" : "🧁"} {t}{" "}
              <span className={styles.mDot}>✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════
          WHAT YOU'LL FIND
      ══════════════════════════════ */}
      <section className={styles.section}>
        <div className="container">
          <SectionHead
            title="What you'll find here"
            sub="A little bit of everything, a whole lot of heart."
          />
          <div className={styles.hobbyGrid}>
            {HOBBIES.map((h, i) => (
              <Link
                key={h.label}
                to={h.to}
                className={styles.hobbyCard}
                style={{ background: h.bg, animationDelay: `${i * 0.1}s` }}
              >
                <span className={styles.hEmoji}>{h.emoji}</span>
                <h3 className={styles.hLabel}>{h.label}</h3>
                <p className={styles.hDesc}>{h.desc}</p>
                <span className={styles.hArrow}>Explore →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════
          TABBED CONTENT FEED
      ══════════════════════════════ */}
      <section className={styles.feedSection}>
        <div className="container">
          <div className={styles.feedHead}>
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${tab === "recipes" ? styles.tabActive : ""}`}
                onClick={() => setTab("recipes")}
              >
                🧁 Latest Recipes
              </button>
              <button
                className={`${styles.tab} ${tab === "dev" ? styles.tabActive : ""}`}
                onClick={() => setTab("dev")}
              >
                💻 Dev Posts
              </button>
            </div>
            <Link
              to={tab === "recipes" ? "/baking" : "/dev"}
              className={styles.seeAll}
            >
              See all →
            </Link>
          </div>
          <div className="grid-3">
            {tab === "recipes"
              ? featuredR.map((r, i) => (
                  <RecipeCard
                    key={r.id}
                    recipe={r}
                    style={{ animationDelay: `${i * 0.08}s` }}
                  />
                ))
              : featuredP.map((p, i) => (
                  <DevCard
                    key={p.id}
                    post={p}
                    style={{ animationDelay: `${i * 0.08}s` }}
                  />
                ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════
          DARK HACKS SECTION
      ══════════════════════════════ */}
      <section className={styles.darkSection}>
        <div className={styles.darkGrid} aria-hidden="true" />
        <div className="container">
          <SectionHead
            title="⚡ Dev Hack of the Week"
            sub="Quick code tips that change how you think. One-click copy on every snippet."
            dark
          />
          <div className={styles.hackLayout}>
            <div className={styles.hackCardWrap}>
              <HackCard hack={previewH} dark />
            </div>
            <div className={styles.hackRight}>
              <div className={styles.bigNum}>
                <span className={styles.bigNumN}>{devHacks.length}</span>
                <span className={styles.bigNumL}>
                  hacks
                  <br />
                  published
                </span>
              </div>
              <p className={styles.hackRightSub}>
                JavaScript, React, CSS, Git & TypeScript - all with
                copy-paste-ready snippets.
              </p>
              <Link
                to="/hacks"
                className={styles.btnPrimary}
                style={{ display: "inline-flex" }}
              >
                Browse all hacks ⚡
              </Link>
              <div className={styles.catPills}>
                {["JavaScript", "React", "CSS", "Git", "TypeScript"].map(
                  (c) => (
                    <Link key={c} to="/hacks" className={styles.catPill}>
                      {c}
                    </Link>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════
          ABOUT STRIP
      ══════════════════════════════ */}
      <section className={styles.aboutStrip}>
        <div className="container">
          <div className={styles.aboutInner}>
            <div className={styles.aboutLeft}>
              <div className={styles.aboutAvatar}>
                <img
                  src={profile.avatar}
                  alt="Avantika Bisht"
                  style={{
                    width: 110,
                    height: 110,
                    borderRadius: "50%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  className="aboutAvatarImg"
                />
              </div>
              <div className={styles.aboutAvatarGlow} />
            </div>
            <div className={styles.aboutContent}>
              <h2 className={styles.aboutTitle}>
                Hey, I'm {profile.name.split(" ")[0]} 👋
              </h2>
              <p className={styles.aboutText}>{profile.bio?.[0]}</p>
              <div className={styles.aboutBtns}>
                <Link to="/about" className={styles.btnPrimary}>
                  Read My Story →
                </Link>
                <a
                  href={`mailto:${profile.email}`}
                  className={styles.btnSecondary}
                >
                  Work With Me
                </a>
              </div>
            </div>
            <div className={styles.skillBars}>
              {[
                { label: "React / Next.js", pct: 92 },
                // {label:'Sourdough',        pct:88},
                { label: "TypeScript", pct: 80 },
                { label: "Cookies", pct: 75 },
              ].map(({ label, pct }) => (
                <div key={label} className={styles.skillBar}>
                  <div className={styles.skillBarTop}>
                    <span className={styles.skillBarLabel}>{label}</span>
                    <span className={styles.skillBarPct}>{pct}%</span>
                  </div>
                  <div className={styles.skillBarTrack}>
                    <div
                      className={styles.skillBarFill}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* <NewsletterSection /> */}
    </div>
  );
}
